import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  Observable,
  catchError,
  finalize,
  map,
  shareReplay,
  switchMap,
  throwError,
} from 'rxjs';
import { omitAutherisation, QTED_URL } from '../../auth/config/constants';
import { IGETRefreshToken, IRefreshToken } from '../../auth/models/IUser';
import { AuthService } from '../../auth/services/auth.service';
import { StorageService } from '../helpers/storage.service';
import { Storagekeys } from '../model/storage';
import { LogLevel, LoggerService } from '../services/logger.service';

const TOKEN_EXPIRY_BUFFER_SECONDS = 40;
const REFRESH_ATTEMPT_HEADER = 'X-Kunnect-Refresh-Attempted';
let refreshInFlight$: Observable<string> | null = null;

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const storageService = inject(StorageService);
  const loggerService = inject(LoggerService);
  const authService = inject(AuthService);
  const snackBar = inject(MatSnackBar);

  const isNoAuthEndpoint = omitAutherisation.some(url => req.url.includes(url));
  if (isNoAuthEndpoint) {
    return next(req).pipe(
      catchError((err: HttpErrorResponse) => {
        handleError(err, snackBar, loggerService);
        return throwError(() => err);
      })
    );
  }

  const sendAuthorizedRequest = (request: HttpRequest<unknown>, token?: string) =>
    next(getHeaders(request, storageService, token));

  if (isRefreshEndpoint(req.url)) {
    // Refresh endpoints need auth headers, but must not trigger refresh recursion.
    return sendAuthorizedRequest(req).pipe(
      catchError((err: HttpErrorResponse) => {
        handleError(err, snackBar, loggerService);
        return throwError(() => err);
      })
    );
  }

  const refreshAndRetry = (request: HttpRequest<unknown>) =>
    refreshAccessToken(authService, storageService).pipe(
      switchMap(token => {
        const retriedRequest = request.clone({
          headers: request.headers.set(REFRESH_ATTEMPT_HEADER, 'true'),
        });
        return sendAuthorizedRequest(retriedRequest, token);
      })
    );

  const with401Handling = (request: HttpRequest<unknown>) =>
    sendAuthorizedRequest(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status !== 401) {
          handleError(err, snackBar, loggerService);
          return throwError(() => err);
        }
        const currentToken = storageService.getItem(Storagekeys.token, true);
        if (!currentToken) {
          authService.logoutEvent.emit(
            'Auto Logout From Tenant Due To Missing Access Token'
          );
          return throwError(() => err);
        }

        if (
          request.headers.get(REFRESH_ATTEMPT_HEADER) === 'true' ||
          isRefreshEndpoint(request.url)
        ) {
          authService.logoutEvent.emit(
            'Auto Logout From Tenant Due To Refresh API Failed With 401'
          );
          return throwError(() => err);
        }

        return refreshAndRetry(request).pipe(
          catchError(refreshErr => {
            handleError(refreshErr as HttpErrorResponse, snackBar, loggerService);
            authService.logoutEvent.emit(
              'Auto Logout From Tenant Due To Refresh Token Recovery Failed'
            );
            return throwError(() => refreshErr);
          })
        );
      })
    );

  const accessToken = storageService.getItem(Storagekeys.token, true);
  if (shouldRefreshProactively(accessToken)) {
    return refreshAndRetry(req).pipe(
      // If proactive refresh fails, send request once and rely on single 401 path.
      // This avoids repeated refresh attempts for the same request chain.
      catchError(() => with401Handling(req))
    );
  }

  return with401Handling(req);
};

function refreshAccessToken(
  authService: AuthService,
  storageService: StorageService
): Observable<string> {
  if (refreshInFlight$) {
    return refreshInFlight$;
  }

  const refreshToken = storageService.getItem(Storagekeys.refreshToken, true);
  if (!refreshToken) {
    authService.logoutEvent.emit(
      'Auto Logout From Tenant Due To Lack Of Refresh Token'
    );
    return throwError(() => new Error('No refresh token available'));
  }

  refreshInFlight$ = authService.getRefreshToken().pipe(
    switchMap((tokenDetails: IGETRefreshToken) => {
      const latestRefreshToken =
        tokenDetails?.refreshToken ?? tokenDetails?.RefreshToken;

      if (latestRefreshToken) {
        storageService.storeItem(
          Storagekeys.refreshToken,
          latestRefreshToken,
          true
        );
      }
      // GetRefreshTokenV2 is used only to sync latest refresh token.
      // Access token is always issued by RefreshToken endpoint.
      return authService.refreshToken();
    }),
    map((authDetails: IRefreshToken) => {
      storageService.storeItem(Storagekeys.token, authDetails.token, true);
      storageService.storeItem(
        Storagekeys.refreshToken,
        authDetails.refreshToken,
        true
      );
      return authDetails.token;
    }),
    finalize(() => {
      refreshInFlight$ = null;
    }),
    shareReplay(1)
  );

  return refreshInFlight$;
}

function isRefreshEndpoint(url: string): boolean {
  return (
    url.includes('UserLogin/RefreshToken') ||
    url.includes('UserLogin/GetRefreshTokenV2')
  );
}

function shouldRefreshProactively(token: string): boolean {
  if (!token) {
    return false;
  }
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) {
    return false;
  }
  const nowInSeconds = Math.floor(Date.now() / 1000);
  return payload.exp - nowInSeconds <= TOKEN_EXPIRY_BUFFER_SECONDS;
}

function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const normalized =
      base64 + '='.repeat((4 - (base64.length % 4 || 4)) % 4);
    return JSON.parse(atob(normalized));
  } catch {
    return null;
  }
}

function getHeaders(
  req: HttpRequest<unknown>,
  storageService: StorageService,
  tokenOverride?: string
): HttpRequest<unknown> {
  const accessToken =
    tokenOverride ?? storageService.getItem(Storagekeys.token, true);
  const accessSessionId = storageService.getItem(Storagekeys.sessionId, true);

  if (
    req.url.includes('api.zippopotam.us') ||
    req.url.includes('amazonaws.com')
  ) {
    // Never attach internal auth/session headers to external services.
    return req.clone();
  }

  if (req.url.includes(QTED_URL.QTED_DOMAIN)) {
    const qtedHeaders: Record<string, string> = {
      token: `${accessToken}`,
      Accept: 'application/json',
    };
    if (!(req.body instanceof FormData)) {
      qtedHeaders['Content-Type'] = 'application/json; charset=utf-8';
    }
    return req.clone({ headers: new HttpHeaders(qtedHeaders) });
  }

  const internalHeaders: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    'X-Kunnect-Sid': `${accessSessionId}`,
    'X-Service-id': String(storageService.getItem(Storagekeys.orgId, true)),
    Accept: 'application/json',
  };
  if (!(req.body instanceof FormData)) {
    internalHeaders['Content-Type'] = 'application/json; charset=utf-8';
  }

  return req.clone({
    headers: new HttpHeaders(internalHeaders),
  });
}

function handleError(
  err: HttpErrorResponse,
  snackBar: MatSnackBar,
  loggerService: LoggerService
) {
  if (!(err instanceof HttpErrorResponse)) {
    loggerService.log(LogLevel.ERROR, err);
    return;
  }

  if (err.status === 500) {
    const message =
      err?.error ??
      'Something went wrong, unable to process your request, check your request or please try after sometime.';
    snackBar.open(message, 'close', {
      duration: 5000,
      panelClass: 'errorSnack',
    });
  }
  loggerService.log(LogLevel.ERROR, err);
}
